import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { LogIn, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { startWhop } from '../lib/whop';
import api from '../lib/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { LogoMark } from '../components/Logo';
import { FullPageLoader } from '../components/ui/Spinner';

export default function Landing() {
  const { user, access, loading, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [completing, setCompleting] = useState(false);
  const [showStaff, setShowStaff] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [otcInput, setOtcInput] = useState('');
  const [otcBusy, setOtcBusy] = useState(false);

  const splitOrigin = (() => {
    try { return new URL(process.env.REACT_APP_BACKEND_URL).origin !== window.location.origin; }
    catch { return true; }
  })();

  // Handle Whop web callback (?whop_otc=...)
  useEffect(() => {
    const otc = params.get('whop_otc');
    if (!otc) return;
    setCompleting(true);
    (async () => {
      try {
        const { data } = await api.post('/auth/whop/complete', { otc });
        await loginWithToken(data.token);
        navigate('/app', { replace: true });
      } catch (e) {
        setCompleting(false);
        setParams({}, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading && user && access && !params.get('whop_otc')) {
      navigate('/app', { replace: true });
    }
  }, [loading, user, access, navigate, params]);

  const staffLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      await loginWithToken(data.token);
      navigate('/app', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid credentials');
    } finally { setBusy(false); }
  };

  const completeWhop = async (e) => {
    e.preventDefault();
    const raw = otcInput.trim();
    if (!raw) return;
    const m = raw.match(/(?:whop_otc|otc)=([^&\s]+)/);
    const otc = m ? decodeURIComponent(m[1]) : raw;
    setOtcBusy(true);
    try {
      const { data } = await api.post('/auth/whop/complete', { otc });
      await loginWithToken(data.token);
      navigate('/app', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid or expired Whop code');
    } finally { setOtcBusy(false); }
  };

  if (completing || (loading && params.get('whop_otc'))) return <FullPageLoader />;

  return (
    <div className="grain relative grid min-h-screen place-items-center overflow-hidden bg-ink-base px-6 text-white" data-testid="landing-page">
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-[-10%] h-[46vh] w-[46vh] -translate-x-1/2 rounded-full bg-emerald/25 blur-[120px]" />
        <div className="absolute bottom-[-15%] right-[-5%] h-[38vh] w-[38vh] rounded-full bg-emerald/10 blur-[120px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="relative z-10 w-full max-w-sm text-center"
      >
        {/* Logo */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.05, duration: 0.5 }}
          className="mx-auto mb-6 grid h-24 w-24 place-items-center rounded-3xl border border-emerald/30 bg-emerald/5 shadow-[0_0_40px_-8px_rgba(21,228,2,0.5)]"
        >
          <LogoMark size={64} />
        </motion.div>

        <h1 className="font-heading text-4xl font-extrabold tracking-tight">
          Stock<span className="text-emerald">Method</span>
        </h1>
        <p className="mt-2 text-sm text-zinc-400">Trade with a method, not luck.</p>

        {/* Actions */}
        <div className="mt-10 space-y-4 text-left">
          <Button pill size="lg" variant="emerald" className="w-full justify-center" onClick={startWhop} data-testid="whop-login-button">
            <LogIn size={18} /> Continue with Whop
          </Button>

          <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest text-zinc-600">
            <div className="h-px flex-1 bg-white/10" /> or <div className="h-px flex-1 bg-white/10" />
          </div>

          {!showStaff ? (
            <button
              onClick={() => setShowStaff(true)}
              data-testid="show-staff-login"
              className="w-full rounded-full border border-white/10 py-3 text-center text-sm text-zinc-300 transition-colors hover:border-white/25 hover:text-white"
            >
              Staff sign in
            </button>
          ) : (
            <motion.form
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              onSubmit={staffLogin}
              className="space-y-3"
            >
              <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
              <Input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
              <Button type="submit" size="lg" className="w-full justify-center" disabled={busy} data-testid="login-submit-button">
                {busy ? 'Signing in…' : 'Sign in'}
              </Button>
              <button type="button" onClick={() => setShowStaff(false)} className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-white">
                <ArrowLeft size={13} /> Back
              </button>
            </motion.form>
          )}

          {splitOrigin && (
            <div className="rounded-xl border border-white/5 bg-ink-surface/60 p-4">
              <p className="text-xs font-medium text-zinc-300">Finish Whop sign-in (preview only)</p>
              <p className="mt-1 text-[11px] leading-relaxed text-zinc-500">
                After “Continue with Whop”, your browser lands on the backend domain at
                <span className="text-zinc-400"> …/?whop_otc=CODE</span>. Paste that URL (or code) below.
              </p>
              <form onSubmit={completeWhop} className="mt-3 flex gap-2">
                <Input placeholder="Paste redirect URL or code" value={otcInput} onChange={(e) => setOtcInput(e.target.value)} className="h-10" data-testid="whop-otc-input" />
                <Button type="submit" size="sm" variant="emerald" disabled={otcBusy} data-testid="whop-otc-submit">{otcBusy ? '…' : 'Finish'}</Button>
              </form>
            </div>
          )}
        </div>

        <p className="mt-10 text-[11px] text-zinc-600">Access secured &amp; verified via Whop.</p>
      </motion.div>
    </div>
  );
}
